import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  inject,
  OnInit,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@ngneat/transloco';
import { ErrorsService } from '../../../core/services/errors.service';
import { ModalName, ModalService } from '../../../core/services/modal.service';
import { QuestionnaireService } from '../../../core/services/questionnaire.service';
import { Questionnaire } from '../../../core/models/questionnaire/questionnaire.model';
import { Question } from '../../../core/models/questionnaire/question.model';
import { QuestionType } from '../../../core/models/questionnaire/question-type.enum';
import { QuestionnaireAnswer } from '../../../core/models/questionnaire/questionnaire-answer.model';
import { ToastService } from '../../../core/services/toast.service';
import { Subscription, tap } from 'rxjs';
import { ParsedErrorResponse } from '../../../core/models/parsed-error-response.model';
import { IconNameType } from '../../../shared/icons/icon-name-type.enum';
import { RoundIconComponent } from '../../../shared/round-icon/round-icon.component';
import { RatingRowComponent } from './rating-row/rating-row.component';
import { AsyncButtonDirective } from '../../../shared/directives/async-button.directive';
import { IconComponent } from '../../../shared/icons/icon.component';
import { AuditService } from '../../../core/services/audit.service';
import { QuestionnaireResponse } from '../../../core/models/questionnaire/questionnaire-response.model';
import { GenericObject } from '../../../core/models/core/generic-object';
import { A11yModule } from "@angular/cdk/a11y";

const LOW_RATING_THRESHOLD = 3;

type SurveyStep = 'rating' | 'follow-up';

@Component({
  selector: 'cwo-modal-survey-checkout',
  imports: [
    RatingRowComponent,
    RoundIconComponent,
    TranslocoDirective,
    AsyncButtonDirective,
    IconComponent,
    FormsModule,
    A11yModule
],
  templateUrl: './modal-survey-checkout.component.html',
  styleUrl: './modal-survey-checkout.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalSurveyCheckoutComponent implements OnInit {
  private readonly questionnaireService = inject(QuestionnaireService);
  private readonly toastService = inject(ToastService);
  private transloco = inject(TranslocoService);
  private errorsService = inject(ErrorsService);
  private auditService = inject(AuditService);
  private modalService = inject(ModalService);

  private readonly answers = signal<GenericObject<QuestionnaireResponse>>({});

  questionnaire?: Questionnaire;
  orderId?: string;

  readonly introText = signal('');
  readonly submitText = signal('');
  readonly questions = signal<Question[]>([]);
  readonly followUpQuestion = signal<Question | undefined>(undefined);
  readonly additionalFeedback = signal<string>('');
  readonly step = signal<SurveyStep>('rating');

  private readonly followUpTextareaRef =
    viewChild<ElementRef<HTMLTextAreaElement>>('followUpTextarea');

  loadingSubscription: Subscription | null = null;
  serverErrors?: ParsedErrorResponse;

  iconNameType = IconNameType;

  ngOnInit() {
    const state = this.modalService.stateForModal(ModalName.EnjoySurvey);

    if (state) {
      this.orderId = state.orderId;
      this.questionnaire = state.questionnaire;

      if (this.questionnaire) {
        this.introText.set(this.questionnaire.introText);
        this.submitText.set(this.questionnaire.submitValue);
        const allQuestions = this.questionnaire.categories.flatMap(
          (category) => category.questions,
        );
        this.questions.set(
          allQuestions.filter((q) => q.questionType === QuestionType.RATING),
        );
        this.followUpQuestion.set(
          allQuestions.find((q) => q.questionType === QuestionType.LONG_TEXT),
        );
      }
    }
  }

  onAnswer(questionId: string, index: number): void {
    const rating = String(index + 1);
    this.answers.update((prev) => ({
      ...prev,
      [questionId]: { answer: rating },
    }));
  }

  onFollowUpTextChange(value: string): void {
    this.additionalFeedback.set(value ?? '');
  }

  onSubmitRatings(): void {
    if (this.loadingSubscription || !this.questionnaire || !this.orderId)
      return;

    if (this.followUpQuestion() && this.hasLowRating()) {
      this.step.set('follow-up');
      setTimeout(() => this.followUpTextareaRef()?.nativeElement.focus());
      return;
    }

    this.finalizeSubmit();
  }

  onSubmitFollowUp(): void {
    const text = this.additionalFeedback().trim();

    if (text && this.followUpQuestion()) {
      this.answers.update((prev) => ({
        ...prev,
        [this.followUpQuestion()!.id]: { answer: text },
      }));
    }

    this.finalizeSubmit();
  }

  private finalizeSubmit(): void {
    if (this.loadingSubscription || !this.questionnaire || !this.orderId)
      return;

    const answer = QuestionnaireAnswer.fromQuestionnaire(
      this.questionnaire,
      this.orderId,
    );

    answer.responses = this.answers();

    this.loadingSubscription = this.questionnaireService
      .answerQuestionnaire(answer)
      .pipe(
        tap(() => {
          this.toastService.showSuccess(
            this.transloco.translate('modals.survey.all_feedback_considered'),
          );
        }),
      )
      .subscribe({
        next: () => {
          this.loadingSubscription = null;
          this.closeModal();
        },
        error: (error) => {
          this.loadingSubscription = null;
          this.serverErrors = this.errorsService?.parseErrorResponse(error);
          this.auditService.createAudit(
            () =>
              `Error submitting Ordering Experience answers: ${this.serverErrors}`,
          );
        },
      });
  }

  private hasLowRating(): boolean {
    return this.questions().some((question) => {
      const answer = this.answers()[question.id]?.answer;
      return answer !== undefined && Number(answer) <= LOW_RATING_THRESHOLD;
    });
  }

  allQuestionsAnswered(): boolean {
    const ans = this.answers();
    return this.questions().every((q) => q.id in ans);
  }

  @HostListener('document:keydown.escape')
  handleEscapeKey() {
    this.onCloseOrSkip();
  }

  onCloseOrSkip(): void {
    if (this.step() === 'follow-up') {
      this.finalizeSubmit();
      return;
    }

    this.closeModal();
  }

  closeModal() {
    this.modalService.closeModal(ModalName.EnjoySurvey);
  }
}
